import React, { useState } from 'react';

import {
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Input,
  LoadingOverlay,
  MantineTheme,
  NumberInput,
  Paper,
  ScrollArea,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Drawer from 'components/Drawer';
import { useAuth } from 'context/auth-context';
import useCreateDocument from 'hooks/document/useCreateDocument';
import useDocument from 'hooks/document/useDocument';
import useUpdateDocument from 'hooks/document/useUpdateDocument';
import useGym from 'hooks/gym/useGym';
import usePackages from 'hooks/package/usePackages';
import { PackageListView } from 'types';
import notificationHandler from 'utils/notifications';
import { Image } from 'utils/tip-tap-image-extension';
import { v4 as uuidv4 } from 'uuid';

const templateVariables: { [key: string]: string } = {
  'Athlete Name': 'ATHLETE_NAME',
  'Athlete Address': 'ATHLETE_ADDRESS',
  'Athlete Email Address': 'ATHLETE_EMAIL_ADDRESS',
  'Athlete Phone Number': 'ATHLETE_PHONE_NUMBER',
  'Athlete Date Of Birth': 'ATHLETE_DATE_OF_BIRTH',
  'Athlete Gender': 'ATHLETE_GENDER',
  'Gym Name': 'GYM_NAME',
  'Gym Address': 'GYM_ADDRESS',
  'Gym Logo': 'GYM_LOGO',
  [`Today's Date`]: 'TODAYS_DATE',
  'Package Details': 'PACKAGE_DETAILS',
  'Package Billing Schedule': 'PACKAGE_BILLING_SCHEDULE',
  'Package Billing Installment Details': 'PACKAGE_INSTALLMENTS',
  'Request Athlete Initials': 'REQUEST_ATHLETE_INITIALS',
};

interface IDrawerContractFormProps {
  formId: string;
  opened: boolean;
  documentId?: string | null;
  closeHandler: () => void;
  permission?: boolean;
}

interface FormValues {
  documentId: string;
  description: string;
  contents: string;
  requireSignatureForAttendance: boolean;
  generateWaiverOnAthleteCreate: boolean;
  packages: string[];
}

const DrawerContractTemplateForm = ({
  formId,
  opened,
  documentId,
  closeHandler,
  permission = true,
}: IDrawerContractFormProps): JSX.Element => {
  const { session } = useAuth();

  const {
    data: prevPackages,
    isLoading: isPrevPackagesLoading,
    isFetching: isPrevPackageFetching,
  } = usePackages({ limit: '1000', contract: documentId || '' });
  const {
    data: packagesData,
    isLoading: isPackagesLoading,
    isFetching: isPackagesFetching,
  } = usePackages({ limit: '1000' });
  const { data: documentData, isLoading: isDocumentLoading } = useDocument(documentId);
  const { data: gymData, isLoading: isGymLoading } = useGym(session?.gym?.gymId);

  const createMutation = useCreateDocument();
  const updateMutation = useUpdateDocument();

  const [packages, setPackages] = useState<PackageListView[]>([]);
  const [showPackageWithContract, setShowPackageWithContract] = useState(false);
  const [showPackageWithCurrentContract, setShowPackageWithCurrentContract] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [logoWidth, setLogoWidth] = useState(200);
  const [logoHeight, setLogoHeight] = useState(100);

  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      documentId: '',
      description: '',
      contents: '',
      requireSignatureForAttendance: false,
      packages: [],
      generateWaiverOnAthleteCreate: false,
    },

    validate: {
      description: value =>
        !value || value.length > 255 ? 'Description must be between 1-255 characters.' : null,
      contents: value => (!value || value === '<p></p>' ? 'This field may not be blank.' : null),
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline as AnyExtension,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'document-gym-logo',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: form.getInputProps('contents').value,
    onUpdate: ({ editor: _editor }) => form.getInputProps('contents').onChange(_editor.getHTML()),
    editable: permission,
  });

  const updateImgAttributes = (attributeName: 'width' | 'height', attributeValue: number): void => {
    // Create a temporary DOM element to parse the HTML string
    const documentToParse = document.createElement('div');
    documentToParse.innerHTML = editor?.getHTML() || '';

    // Get all img elements in the temporary element
    const imgElements = documentToParse.querySelectorAll('img');

    // Loop through each img element and update the specified attribute
    imgElements.forEach(imgElement => {
      imgElement.setAttribute(attributeName, String(attributeValue));
    });

    // Get the updated HTML string
    const updatedHtmlText = documentToParse.innerHTML;

    // Update the editor and form with the modified HTML
    editor?.commands.setContent(updatedHtmlText);
    form.getInputProps('contents').onChange(updatedHtmlText);
  };

  const handleLogoWidthChange = (width: number): void => {
    setLogoWidth(width);
    updateImgAttributes('width', width);
  };

  const handleLogoHeightChange = (height: number): void => {
    setLogoHeight(height);
    updateImgAttributes('height', height);
  };

  const handlePackageChecked = (packageId: string): void => {
    const packageIndex = form.values.packages.indexOf(packageId);
    if (packageIndex === -1) {
      form.setFieldValue('packages', [...form.values.packages, packageId]);
    } else {
      form.setFieldValue(
        'packages',
        form.values.packages.filter((_, index) => index !== packageIndex),
      );
    }
  };

  const handleTemplateVariableClick = (variable: string): void => {
    const templateToInsert = templateVariables[variable];

    if (templateToInsert === 'GYM_LOGO') {
      if (!!gymData?.data.profilePicture) {
        editor
          ?.chain()
          .setImage({
            src: gymData?.data.profilePicture,
            width: String(logoWidth),
            height: String(logoHeight),
          })
          .focus()
          .run();
      } else {
        errorNotification("Gym Logo doesn't exist", 'This action is disabled until you upload gym logo.');
      }
    } else {
      editor?.chain().insertContent(`${templateToInsert} `).focus().run();
    }
  };

  const boldSearchTermInLabel = (packageName: string): JSX.Element | string => {
    const searchTermIndex = packageName.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (searchTermIndex === -1) {
      return packageName;
    }
    return (
      <>
        {packageName.substring(0, searchTermIndex)}
        <strong>{packageName.substring(searchTermIndex, searchTermIndex + searchTerm.length)}</strong>
        {packageName.substring(searchTermIndex + searchTerm.length)}
      </>
    );
  };

  const packageToCheckbox = (kiloPackage: PackageListView): JSX.Element | null => {
    if (
      kiloPackage.contract === null &&
      kiloPackage.name.toLowerCase().includes(searchTerm.toLowerCase() || '')
    ) {
      return (
        <Checkbox
          key={kiloPackage.packageId}
          disabled={!permission}
          label={boldSearchTermInLabel(kiloPackage.name)}
          value={kiloPackage.packageId}
          onChange={() => handlePackageChecked(kiloPackage.packageId)}
          checked={form.values.packages.includes(kiloPackage.packageId)}
          data-cy='packageCheckBox'
        />
      );
    }
    if (documentData?.data && kiloPackage.contract === documentData.data.documentId) {
      return (
        <Checkbox
          key={kiloPackage.packageId}
          disabled={!permission}
          label={boldSearchTermInLabel(kiloPackage.name)}
          value={kiloPackage.packageId}
          onChange={() => handlePackageChecked(kiloPackage.packageId)}
          checked={form.values.packages.includes(kiloPackage.packageId)}
          data-cy='packageCheckBoxDocuments'
        />
      );
    }
    if (
      showPackageWithContract &&
      kiloPackage.contract !== null &&
      kiloPackage.name.toLowerCase().includes(searchTerm.toLowerCase() || '')
    ) {
      return (
        <Checkbox
          key={kiloPackage.packageId}
          label={boldSearchTermInLabel(kiloPackage.name)}
          value={kiloPackage.packageId}
          disabled
          checked
        />
      );
    }
    return null;
  };

  const packageToCheckboxCurrentContract = (kiloPackage: PackageListView): JSX.Element | null => {
    if (
      form.values.packages.includes(kiloPackage.packageId) &&
      kiloPackage.name.toLowerCase().includes(searchTerm.toLowerCase() || '')
    ) {
      return (
        <Checkbox
          key={kiloPackage.packageId}
          label={boldSearchTermInLabel(kiloPackage.name)}
          value={kiloPackage.packageId}
          onChange={() => handlePackageChecked(kiloPackage.packageId)}
          checked={form.values.packages.includes(kiloPackage.packageId)}
          disabled={!permission}
        />
      );
    }
    return null;
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    editor?.commands.setContent('');
    setShowPackageWithContract(false);
    setShowPackageWithCurrentContract(false);
    setSearchTerm('');
  };

  const handleSubmit = (values: FormValues): void => {
    const payload = {
      description: values.description,
      requireSignatureForAttendance: form.values.requireSignatureForAttendance,
      generateWaiverOnAthleteCreate: form.values.generateWaiverOnAthleteCreate,
      contents: form.values.contents,
      isTemplate: true,
      packages: form.values.packages,
      athlete: null,
    };
    if (documentData?.data) {
      updateMutation.mutate(
        {
          ...payload,
          type: 'contract',
          documentId: documentData.data.documentId,
        },
        {
          onError: error => {
            errorNotification('Contract Template Update Failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification(
              'Contract Template Updated',
              'Contract Template has been updated successfully',
            );
            handleCloseAndReset();
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          ...payload,
          documentId: uuidv4(),
          type: 'contract',
        },
        {
          onError: error => {
            errorNotification('Contract Template Creation Failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification(
              'Contract Template Created',
              'Contract Template has been created successfully',
            );
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (documentId && documentData?.data && opened && packagesData) {
      form.setValues({ ...documentData.data });
      editor?.commands.setContent(documentData.data.contents);
      if (opened && prevPackages && !isPrevPackagesLoading && !isPrevPackageFetching) {
        form.setFieldValue(
          'packages',
          prevPackages?.pages[0].data.results.map(kiloPackage => kiloPackage.packageId),
        );
      }

      if (documentData.data.contents.includes('img')) {
        // Create a temporary DOM element to parse the HTML string
        const documentToParse = document.createElement('div');
        documentToParse.innerHTML = documentData.data.contents;
        // Get all img elements in the temporary element
        const imgElement = documentToParse.querySelectorAll('img')[0];
        const initialWidthStr = imgElement.getAttribute('width');
        const initialHeightStr = imgElement.getAttribute('height');
        if (!!initialWidthStr) {
          setLogoWidth(Number(initialWidthStr));
        }
        if (!!initialHeightStr) {
          setLogoHeight(Number(initialHeightStr));
        }
      }
    }
    if (!isPackagesLoading && !isPackagesFetching && packagesData && opened) {
      setPackages(packagesData.pages.map(page => page.data.results).flat());
    }
  }, [
    documentId,
    prevPackages,
    opened,
    packagesData?.pages,
    isPrevPackageFetching,
    isPrevPackagesLoading,
    isPackagesLoading,
    isPackagesFetching,
  ]);

  return (
    <Drawer
      opened={opened}
      title={<Title order={3}>{documentData?.data.description || 'Create Contract Template'}</Title>}
      onClose={handleCloseAndReset}
      size={900}
      footer={
        <Group position='right'>
          <Button variant='subtle' mr='sm' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            type='submit'
            form={formId}
            disabled={!permission || isPackagesLoading || isDocumentLoading}
            loading={createMutation.isLoading || updateMutation.isLoading}
            data-cy='saveContractTempButton'
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay
        visible={
          isPackagesLoading ||
          isPackagesFetching ||
          isPrevPackageFetching ||
          isPrevPackagesLoading ||
          isGymLoading
        }
      />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Grid>
          <Grid.Col span='auto'>
            <Stack spacing='xl'>
              <TextInput
                label='Description'
                {...form.getInputProps('description')}
                required
                disabled={!permission}
                data-cy='createContractDescriptionInput'
              />
              <Checkbox
                disabled={!permission}
                label='Require signed contract for athlete reservations'
                {...form.getInputProps('requireSignatureForAttendance')}
                checked={form.getInputProps('requireSignatureForAttendance').value ?? false}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span='auto'>
            <Stack>
              <Paper withBorder p='xl'>
                <TextInput
                  disabled={!permission}
                  placeholder='Search Package Name'
                  label='Attached Packages'
                  onChange={event => setSearchTerm(event.currentTarget.value)}
                  sx={{ marginBottom: 10 }}
                  data-cy='searchPackageContractForm'
                />
                <ScrollArea h={150}>
                  <Stack spacing='xs'>
                    {!showPackageWithCurrentContract &&
                      packages.map((kiloPackage: PackageListView) => packageToCheckbox(kiloPackage))}
                    {showPackageWithCurrentContract &&
                      packages.map((kiloPackage: PackageListView) =>
                        packageToCheckboxCurrentContract(kiloPackage),
                      )}
                  </Stack>
                </ScrollArea>
              </Paper>
              <Checkbox
                pl='md'
                label='Show packages assigned to other contracts'
                onChange={() => setShowPackageWithContract(!showPackageWithContract)}
                checked={showPackageWithContract && !showPackageWithCurrentContract}
                disabled={showPackageWithCurrentContract || !permission}
              />
              <Checkbox
                pl='md'
                label='Only show selected packages'
                onChange={() => setShowPackageWithCurrentContract(!showPackageWithCurrentContract)}
                checked={showPackageWithCurrentContract}
                disabled={!permission}
              />
            </Stack>
          </Grid.Col>
        </Grid>
        <Divider mx='-xl' my='lg' />
        <Stack pb='md'>
          <Group spacing='xs'>
            {Object.keys(templateVariables).map(variable => (
              <Button
                key={variable}
                radius='sm'
                size='xs'
                compact
                onClick={() => handleTemplateVariableClick(variable)}
                disabled={!permission}
                data-cy='templateAddOns'
              >
                {variable}
              </Button>
            ))}
          </Group>
        </Stack>
        <RichTextEditor editor={editor} data-cy='contractContent'>
          <RichTextEditor.Toolbar sticky stickyOffset={-24} display={permission ? 'default' : 'none'}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
              <RichTextEditor.H4 />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.OrderedList />
              <RichTextEditor.BulletList />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup sx={(theme: MantineTheme) => ({ gap: theme.spacing.xs })}>
              <Group noWrap spacing='xs'>
                <Input.Label size='xs'>Logo width:</Input.Label>
                <NumberInput
                  value={logoWidth}
                  onChange={handleLogoWidthChange}
                  step={10}
                  min={0}
                  w={100}
                  size='xs'
                />
              </Group>
              <Group noWrap spacing='xs'>
                <Input.Label size='xs'>Logo height:</Input.Label>
                <NumberInput
                  value={logoHeight}
                  onChange={handleLogoHeightChange}
                  step={10}
                  min={0}
                  size='xs'
                  w={100}
                />
              </Group>
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
          <RichTextEditor.Content />
        </RichTextEditor>
      </form>
    </Drawer>
  );
};

export default DrawerContractTemplateForm;
